import React, { useCallback, useMemo, useEffect } from 'react'

import { useClosuresMap } from '../../roadClosures/hooks'

import GraphicLayer from './GraphicLayer'

const getSegmentSymbol = (isHovered, isSelected) => {
  return isHovered
    ? {
        type: 'simple-line', // autocasts as new SimpleLineSymbol()
        color: 'green',
        width: '3px',
        style: 'solid',
      }
    : isSelected
    ? {
        type: 'simple-line', // autocasts as new SimpleLineSymbol()
        color: 'blue',
        width: '3px',
        style: 'solid',
      }
    : {
        type: 'simple-line', // autocasts as new SimpleLineSymbol()
        color: 'red',
        width: '2px',
        style: 'solid',
      }
}

const getSegmentGeometry = (segment) => ({
  type: 'polyline',
  paths: segment.geometry.paths,
})

const getSegmentAttributes = (closure) => ({
  id: closure.id,
})

const RoadClosuresLayer = ({ map, view }) => {
  const [
    { closures, selected, editorIsOpen },
    { select, clear },
  ] = useClosuresMap()

  const getIsSelected = useCallback(
    (graphic) =>
      selected.ids.length &&
      selected.ids[selected.current] === graphic.attributes.id,
    [selected]
  )

  const graphics = useMemo(
    () =>
      editorIsOpen
        ? []
        : closures.flatMap((closure) => {
            const isSelected =
              selected.ids.length &&
              selected.ids[selected.current] === closure.id
            return closure.roadSegments.map((segment) => ({
              geometry: getSegmentGeometry(segment),
              symbol: getSegmentSymbol(segment.hover, isSelected),
              attributes: getSegmentAttributes(closure),
            }))
          }),
    [closures, selected, editorIsOpen]
  )

  const onSelect = useCallback(
    (graphics) => {
      if (editorIsOpen) return
      if (
        !selected.ids.some((id) => graphics.some((g) => g.attributes.id === id))
      ) {
        select(graphics.map((g) => g.attributes.id))
      }
    },
    [select, selected, editorIsOpen]
  )

  const onDeselect = useCallback(() => {
    if (editorIsOpen) return
    clear()
  }, [editorIsOpen, clear])

  // Zoom/pan on selection change
  useEffect(() => {
    if (selected.ids.length) {
      const id = selected.ids[selected.current]
      const graphic = graphics.find((g) => g.attributes.id === id)
      if (graphic && graphic.geometry) {
        view.goTo(graphic.geometry)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, view]) // only update when selection changes

  return (
    <GraphicLayer
      id="roadClosuresLayer"
      map={map}
      view={view}
      graphics={graphics}
      getIsSelected={getIsSelected}
      onSelect={onSelect}
      onDeselect={onDeselect}
    />
  )
}

export default RoadClosuresLayer
